import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
    CENTER_SERVICE_AVAILABILITY_STATUS,
    getCenterServiceAvailabilityStatus,
} from '@/utils/centerServiceAvailability';

interface StudyCafeAccessGuardProps {
    children: ReactNode;
}

export function StudyCafeAccessGuard({ children }: StudyCafeAccessGuardProps) {
    const navigate = useNavigate();
    const { isLoggedIn, isLoading: authLoading, checkAuth } = useAuth();
    const [isChecking, setIsChecking] = useState(true);

    useEffect(() => {
        if (authLoading) {
            return;
        }

        if (!isLoggedIn) {
            setIsChecking(false);
            return;
        }

        let isMounted = true;

        const verifyStudyCafeAccess = async () => {
            setIsChecking(true);

            if (getCenterServiceAvailabilityStatus() === CENTER_SERVICE_AVAILABILITY_STATUS.SERVICE_RESERVATION_BLOCKED) {
                navigate('/reservation-time-restricted', { replace: true });
                return;
            }

            const refreshedUser = await checkAuth({ showLoading: false });

            if (!isMounted) {
                return;
            }

            if (refreshedUser?.serviceAccess?.studyCafeEnabled === false) {
                navigate('/study-cafe/access-restricted', { replace: true });
                return;
            }

            if ((refreshedUser?.fixedStudySeats?.length ?? 0) > 0) {
                navigate('/study-cafe/fixed-seat', { replace: true });
                return;
            }

            setIsChecking(false);
        };

        verifyStudyCafeAccess();

        return () => {
            isMounted = false;
        };
    }, [authLoading, checkAuth, isLoggedIn, navigate]);

    if (authLoading || isChecking) {
        return <div style={{ width: '100vw', height: '100vh', backgroundColor: '#ffffff' }} aria-hidden="true" />;
    }

    return <>{children}</>;
}
